import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, PageHeader, Btn } from "../components/UI";
import { Database, Users2, Sparkles, MapPinned, Compass, ArrowRight, RotateCcw, Vote } from "lucide-react";

const SECTIONS = [
  { key: "political-data", icon: Database, title: "Political Data", body: "Voter file, modeling, issue & policy, civic engagement, and media consumption segments.", to: "/political-data", accent: true },
  { key: "my-data", icon: Users2, title: "My Political Data", body: "First-party voter files, donor lists, and supporter data (with lawful-consent gate).", to: "/first-party" },
  { key: "ai-audiences", icon: Sparkles, title: "Political AI Audiences", body: "AI-modeled persuadables built from your objectives and the available catalog.", to: "/political-data?category=Political%20AI" },
  { key: "contextual", icon: Compass, title: "Political Contextual", body: "Placement adjacent to political news, government, and policy content.", to: "/political-data?category=Political%20Contextual" },
  { key: "geography", icon: MapPinned, title: "Political Geography", body: "Draw district polygons, target by DMA, state, ZIP, or precinct.", to: "/geo" },
  { key: "retargeting", icon: RotateCcw, title: "Retargeting", body: "Website visitors, ad clickers, and supporter opt-ins.", to: "/audiences" },
  { key: "other", icon: Compass, title: "Target Other Categories", body: "Lifestyle, contextual, in-market, professional, and demographic segments approved for political use.", to: "/other-targeting" },
];

export default function TargetVoters() {
  const navigate = useNavigate();
  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Voter Audiences"
        title="Target Voters"
        subtitle="Choose from political data, your own voter/supporter data, geographic targeting, and other approved audience sources."
      />

      {/* AI search hero */}
      <Card className="relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
              <span className="text-xs text-cyan-300 font-medium uppercase tracking-wider">AI Audience Search</span>
            </div>
            <h3 className="font-display text-2xl font-bold tracking-tight">Describe who you want to reach.</h3>
            <p className="mt-1 text-slate-400 text-sm max-w-2xl">"Find TX voters interested in property taxes, education, and local government." Our AI searches only your available political data catalog — never invents segments.</p>
          </div>
          <Btn onClick={() => navigate("/political-data")} data-testid="voter-ai-search-btn">
            <Sparkles className="w-4 h-4" /> Search voter audiences <ArrowRight className="w-4 h-4" />
          </Btn>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            data-testid={`target-section-${s.key}`}
            onClick={() => navigate(s.to)}
            className={`group text-left rounded-xl border p-5 transition-all ${
              s.accent
                ? "border-cyan-400/40 bg-gradient-to-br from-cyan-400/10 to-blue-500/5 hover:border-cyan-400/60"
                : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20"
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              s.accent ? "bg-gradient-to-br from-cyan-400 to-blue-500 text-slate-900" : "bg-white/5 border border-white/10 text-cyan-300"
            }`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div className="mt-4 font-display text-lg font-bold">{s.title}</div>
            <p className="mt-1 text-sm text-slate-400">{s.body}</p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-cyan-300 font-semibold">
              Open <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
